import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import { nanoid } from '@utils/strings'
import { PopoverBag, PopoverSettings } from 'types/ui'

import { usePopperModifiers } from './modifiers'
import { usePopoverAnchor } from './anchor'

export function usePopover({
  placement = 'top',
  flip = true,
  arrow = false,
  closeable = false,
  offset = [0, 8],
  openOnFocus = true,
  closeOnBlur = true,
  openOnAnchorHover = true,
  closeOnAnchorHoverExit = true,
  toggleOpenOnAnchorClick = false,
}: PopoverSettings = {}): PopoverBag {
  // Popper refs and state management
  const [visible, setVisible] = useState<boolean>(false)
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const id = useRef(`popover-${nanoid()}`)
  const visibleRef = useRef(visible)

  const modifiers = usePopperModifiers({ flip, arrow, offset, arrowElement })
  const popper = usePopper(anchorElement, popperElement, {
    placement,
    modifiers,
  })

  const show = useCallback(async () => {
    setVisible(true)
    setTimeout(() => popper.update?.(), 100)
  }, [popper])

  const hide = useCallback(async () => {
    setVisible(false)
    setTimeout(() => popper.update?.(), 100)
  }, [popper])

  usePopoverAnchor({
    id: id.current,
    show,
    hide,
    visible,
    anchorElement,
    openOnFocus,
    closeOnBlur,
    openOnAnchorHover,
    closeOnAnchorHoverExit,
    toggleOpenOnAnchorClick,
  })

  useEffect(() => {
    visibleRef.current = visible
    popper.update?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const props = useMemo(
    () => ({
      id: id.current,
      hide,
      show,
      arrow,
      visible,
      closeable,
      setArrowElement,
      setPopperElement,
      arrowStyles: popper.styles.arrow,
      popperStyles: popper.styles.popper,
      popperAttributes: popper.attributes.popper,
    }),
    [
      arrow,
      closeable,
      hide,
      popper.attributes.popper,
      popper.styles.arrow,
      popper.styles.popper,
      show,
      visible,
    ]
  )

  return {
    setAnchorElement,
    props,
    show,
    hide,
  }
}
